import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Heading,
  useToast,
  Button,
  HStack,
  VStack,
  Grid,
  GridItem,
  Text,
  Divider,
  Skeleton,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Flex,
  useColorModeValue,
  Icon,
  Input,
  Tooltip,
  IconButton,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import {
  BsArrowLeft,
  BsPencil,
  BsCheckCircle,
  BsXCircle,
  BsArrowCounterclockwise,
  BsCheckCircleFill,
  BsXCircleFill,
  BsLightningChargeFill,
  BsInfoCircle,
} from "react-icons/bs";
import { toast as toastify } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import procurementApi from "../services/procurementApi";
import StatusBadge from "../components/StatusBadge";
import LineItemsTable from "../components/LineItemsTable";
import VendorPOProposalList from "../components/VendorPOProposalList";
import ApprovalProgressBar from "../components/ApprovalProgressBar";
import CommentSection from "../components/CommentSection";
import "./RequestDetail.css";

const RequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const benchRemarksRef = useRef("");
  const rejectRemarksRef = useRef("");

  const bgColor = useColorModeValue("white", "gray.800");
  const isRejected = request?.status === "REJECTED";

  useEffect(() => {
    fetchRequest();
  }, [id]);

  const fetchRequest = async () => {
    try {
      setLoading(true);
      const response = await procurementApi.getRequestById(id);
      const requestData = response?.data || response;
      if (!requestData) {
        throw new Error("Request not found");
      }
      setRequest(requestData);
    } catch (error) {
      toast({
        title: "Error fetching request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      navigate("/procurement/requests");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/procurement/requests/${id}/edit`);
  };

  const handleSubmit = async () => {
    try {
      await procurementApi.submitRequest(id);
      toast({
        title: "Request submitted for approval",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchRequest();
    } catch (error) {
      toast({
        title: "Error submitting request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleApprove = async () => {
    try {
      await procurementApi.approveRequest(id, { comments: "Approved" });
      toast({
        title: "Request approved",
        status: "success",
        duration: 2000,
      });
      fetchRequest();
    } catch (error) {
      toast({
        title: "Error approving request",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleReject = () => {
    const executeReject = async (toastId) => {
      toastify.dismiss(toastId);
      const remarks = rejectRemarksRef.current?.trim();
      if (!remarks) {
        toastify.error("Please provide a rejection reason.");
        return;
      }

      try {
        await procurementApi.rejectRequest(id, { remarks });
        toastify.success("Request rejected");
        rejectRemarksRef.current = "";
        fetchRequest();
      } catch (error) {
        toastify.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to reject request",
        );
      }
    };

    const toastId = toastify(
      ({ closeToast }) => (
        <Box>
          <Text fontWeight="bold" mb={2}>
            Reason for rejection:
          </Text>
          <Input
            placeholder="Enter rejection remarks"
            size="sm"
            mb={3}
            onChange={(e) => {
              rejectRemarksRef.current = e.target.value;
            }}
          />
          <HStack spacing={2}>
            <Button
              size="sm"
              colorScheme="red"
              onClick={() => executeReject(toastId)}
            >
              Reject
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                rejectRemarksRef.current = "";
                closeToast();
              }}
            >
              Cancel
            </Button>
          </HStack>
        </Box>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        type: "warning",
      },
    );
  };

  const handleApproveBench = () => {
    const executeBenchApproval = async (toastId) => {
      toastify.dismiss(toastId);
      try {
        await procurementApi.approveBench(id, {
          remarks: benchRemarksRef.current || "",
        });
        toastify.success(
          "Request approved by bench — all workflow stages overridden",
        );
        benchRemarksRef.current = "";
        fetchRequest();
      } catch (error) {
        toastify.error(
          error.response?.data?.error?.message ||
            "Failed to perform bench approval",
        );
      }
    };

    const toastId = toastify(
      ({ closeToast }) => (
        <Box>
          <Text fontWeight="bold" mb={2}>
            Approve by Bench?
          </Text>
          <Text fontSize="sm" mb={3}>
            This will override all remaining approval stages.
          </Text>
          <Input
            placeholder="Remarks (optional)"
            size="sm"
            mb={3}
            onChange={(e) => {
              benchRemarksRef.current = e.target.value;
            }}
          />
          <HStack spacing={2}>
            <Button
              size="sm"
              colorScheme="yellow"
              onClick={() => executeBenchApproval(toastId)}
            >
              Confirm
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                benchRemarksRef.current = "";
                closeToast();
              }}
            >
              Cancel
            </Button>
          </HStack>
        </Box>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        type: "warning",
      },
    );
  };

  if (loading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={4} />
        <Skeleton height="600px" />
      </Box>
    );
  }

  if (!request) {
    return (
      <Box p={6}>
        <Text>Request not found</Text>
      </Box>
    );
  }

  const workflowSteps = (request.workflowId?.stages || []).map(
    (stage, index) => {
      const benchApprovedByHistory = request.approvalHistory?.some(
        (entry) => entry.stage === index && entry.action === "BENCH_APPROVE",
      );

      const approvedByHistory = request.approvalHistory?.some(
        (entry) =>
          entry.stage === index &&
          (entry.action === "APPROVE" || entry.action === "BENCH_APPROVE"),
      );

      const hasActiveCurrentStage =
        request.status === "SUBMITTED" || request.status === "IN_REVIEW";

      const status = benchApprovedByHistory
        ? "bench"
        : approvedByHistory || request.status === "APPROVED"
          ? "completed"
          : hasActiveCurrentStage && index === request.workflowStage
            ? "current"
            : "pending";

      return {
        id: stage._id || `${stage.stageName}-${index}`,
        name: stage.stageName || `Stage ${index + 1}`,
        status,
      };
    },
  );

  return (
    <Box p={{ base: 3, md: 6 }}>
      {/* Header */}
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        mb={6}
        gap={3}
      >
        <HStack mb={{ base: 2, md: 0 }}>
          <Button
            leftIcon={<BsArrowLeft />}
            variant="ghost"
            onClick={() => navigate("/procurement/requests")}
            size={{ base: "sm", md: "md" }}
          >
            Back
          </Button>
          <Heading size={{ base: "md", md: "lg" }}>Request Details</Heading>
        </HStack>
        <HStack
          flexWrap="wrap"
          justify={{ base: "flex-start", md: "flex-end" }}
        >
          {request.status === "DRAFT" && (
            <>
              <Button
                leftIcon={<BsPencil />}
                variant="outline"
                onClick={handleEdit}
                size={{ base: "sm", md: "md" }}
              >
                Edit
              </Button>
              <Button
                leftIcon={<BsCheckCircle />}
                colorScheme="blue"
                onClick={handleSubmit}
                size={{ base: "sm", md: "md" }}
              >
                Submit for Approval
              </Button>
            </>
          )}
          {(request.status === "SUBMITTED" || request.status === "IN_REVIEW") &&
            user &&
            (user.role === "SuperAdmin" ||
              (request.currentApprover &&
                request.currentApprover._id === user._id)) && (
              <>
                <Button
                  leftIcon={<BsCheckCircle />}
                  colorScheme="green"
                  onClick={handleApprove}
                  size={{ base: "sm", md: "md" }}
                >
                  Approve
                </Button>
                <Button
                  leftIcon={<BsXCircle />}
                  colorScheme="red"
                  onClick={handleReject}
                  size={{ base: "sm", md: "md" }}
                >
                  Reject
                </Button>
                <Button
                  leftIcon={<BsLightningChargeFill />}
                  colorScheme="yellow"
                  onClick={handleApproveBench}
                  size={{ base: "sm", md: "md" }}
                >
                  Approve by Bench
                </Button>
                <Tooltip
                  label={
                    <Box p={2}>
                      <Text fontWeight="bold" mb={2}>
                        Approval Button Visibility:
                      </Text>
                      <Text fontSize="sm" mb={1}>
                        • Status must be SUBMITTED or IN_REVIEW
                      </Text>
                      <Text fontSize="sm" mb={1}>
                        • You must be the current approver
                      </Text>
                      <Text fontSize="sm">• OR you have SuperAdmin role</Text>
                    </Box>
                  }
                  placement="bottom"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  fontSize="sm"
                >
                  <IconButton
                    icon={<BsInfoCircle />}
                    variant="ghost"
                    size={{ base: "sm", md: "md" }}
                    aria-label="Approval button info"
                    colorScheme="blue"
                  />
                </Tooltip>
              </>
            )}
          {(request.status === "SUBMITTED" || request.status === "IN_REVIEW") &&
            user &&
            !(
              user.role === "SuperAdmin" ||
              (request.currentApprover &&
                request.currentApprover._id === user._id)
            ) && (
              <Tooltip
                label={
                  <Box p={2}>
                    <Text fontWeight="bold" mb={2}>
                      Why can't I approve?
                    </Text>
                    <Text fontSize="sm" mb={1}>
                      • Approval buttons only show to the current approver
                    </Text>
                    <Text fontSize="sm" mb={1}>
                      • Current Approver:{" "}
                      {request.currentApprover?.name || "N/A"}
                    </Text>
                    <Text fontSize="sm">
                      • SuperAdmins can approve at any stage
                    </Text>
                  </Box>
                }
                placement="bottom"
                hasArrow
                bg="orange.600"
                color="white"
                fontSize="sm"
              >
                <IconButton
                  icon={<BsInfoCircle />}
                  variant="ghost"
                  size={{ base: "sm", md: "md" }}
                  aria-label="Approval button info"
                  colorScheme="orange"
                />
              </Tooltip>
            )}
        </HStack>
      </Flex>
      {isRejected && (
        <Alert status="warning" mb={6} borderRadius="md">
          <AlertIcon />
          This request has been rejected. All actions and edits are disabled.
        </Alert>
      )}
      <Box
        pointerEvents={isRejected ? "none" : "auto"}
        opacity={isRejected ? 0.65 : 1}
      >
        {/* Basic Info Card */}
        <Card mb={6} bg={bgColor}>
          <CardHeader>
            <Flex
              direction={{ base: "column", sm: "row" }}
              justify="space-between"
              align={{ base: "flex-start", sm: "center" }}
              gap={2}
            >
              <Heading size={{ base: "sm", md: "md" }} noOfLines={2}>
                {request.title}
              </Heading>
              <StatusBadge status={request.status} />
            </Flex>
          </CardHeader>
          <CardBody>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
              gap={6}
            >
              {request.IomGeneratedNumber && (
                <GridItem>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                    IOM No.
                  </Text>
                  <Text fontFamily="mono">{request.IomGeneratedNumber}</Text>
                </GridItem>
              )}
              <GridItem>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                  Request ID
                </Text>
                <Text>
                  {request.requestId || request._id?.slice(-8) || "N/A"}
                </Text>
              </GridItem>
              <GridItem>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                  Department
                </Text>
                <Text>{request.department || "N/A"}</Text>
              </GridItem>
              <GridItem>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                  Category
                </Text>
                <Text>{request.category || "N/A"}</Text>
              </GridItem>
              <GridItem>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                  Priority
                </Text>
                {request.priority ? (
                  <Badge
                    colorScheme={
                      request.priority === "URGENT"
                        ? "red"
                        : request.priority === "HIGH"
                          ? "orange"
                          : request.priority === "MEDIUM"
                            ? "blue"
                            : "gray"
                    }
                  >
                    {request.priority}
                  </Badge>
                ) : (
                  <Text>N/A</Text>
                )}
              </GridItem>
              <GridItem>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                  Estimated Budget
                </Text>
                <Text>₹{request.estimatedBudget || "N/A"}</Text>
              </GridItem>
              <GridItem>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                  Expected Delivery
                </Text>
                <Text>
                  {request.expectedDeliveryDate
                    ? new Date(
                        request.expectedDeliveryDate,
                      ).toLocaleDateString()
                    : "N/A"}
                </Text>
              </GridItem>
            </Grid>

            <Divider my={4} />

            <VStack align="stretch" spacing={4}>
              <Box>
                <Text
                  fontWeight="semibold"
                  fontSize="sm"
                  color="gray.600"
                  mb={2}
                >
                  Description
                </Text>
                {request.description && /[<>]/.test(request.description) ? (
                  <Box
                    className="description-content"
                    dangerouslySetInnerHTML={{ __html: request.description }}
                  />
                ) : (
                  <Text>
                    {request.description || "No description provided"}
                  </Text>
                )}
              </Box>

              {request.justification && (
                <Box>
                  <Text
                    fontWeight="semibold"
                    fontSize="sm"
                    color="gray.600"
                    mb={2}
                  >
                    Justification
                  </Text>
                  {/[<>]/.test(request.justification) ? (
                    <Box
                      className="justification-content"
                      dangerouslySetInnerHTML={{
                        __html: request.justification,
                      }}
                    />
                  ) : (
                    <Text>{request.justification}</Text>
                  )}
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Workflow Stage Card - Show for submitted/approved/rejected requests */}
        {request.workflowId && request.status !== "DRAFT" && (
          <Card mb={6} bg={bgColor}>
            <CardHeader>
              <Heading size="md">Approval Workflow</Heading>
            </CardHeader>
            <CardBody>
              {/* Current Stage Info */}
              <VStack align="stretch" spacing={4} mb={6}>
                <Flex
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                  align={{ base: "stretch", md: "center" }}
                  gap={{ base: 4, md: 0 }}
                >
                  <Box>
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      Current Stage
                    </Text>
                    <Text
                      fontSize={{ base: "md", md: "lg" }}
                      fontWeight="semibold"
                    >
                      {request.workflowId?.stages?.[request.workflowStage]
                        ?.stageName || "N/A"}
                    </Text>
                  </Box>
                  {request.currentApprover && (
                    <Box textAlign={{ base: "left", md: "right" }}>
                      <Text fontSize="sm" color="gray.600" mb={1}>
                        Current Approver
                      </Text>
                      <Text fontSize="md" fontWeight="medium">
                        {request.currentApprover?.name || "N/A"}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {request.currentApprover?.email || ""}
                      </Text>
                    </Box>
                  )}
                </Flex>

                {/* Stage Progress Bar */}
                {request.workflowId?.stages && (
                  <Box>
                    {request.workflowId.stages.length === 1 ? (
                      // Single Stage - Direct Approval
                      <VStack spacing={4} align="center" py={4}>
                        <Box textAlign="center">
                          <Badge colorScheme="blue" mb={3}>
                            Direct Approval
                          </Badge>
                        </Box>
                        <Icon
                          as={BsCheckCircleFill}
                          boxSize={16}
                          color="blue.500"
                        />
                        <Box textAlign="center">
                          <Text fontSize="lg" fontWeight="bold" mb={1}>
                            {request.workflowId.stages[0].stageName}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            This request requires approval from a single stage
                          </Text>
                        </Box>
                      </VStack>
                    ) : (
                      <ApprovalProgressBar steps={workflowSteps} />
                    )}
                  </Box>
                )}
              </VStack>

              <Divider my={4} />

              {/* Approval History Timeline */}
              {request.approvalHistory &&
                request.approvalHistory.length > 0 && (
                  <Box>
                    <Text fontWeight="semibold" fontSize="md" mb={4}>
                      Approval History
                    </Text>
                    <VStack align="stretch" spacing={4}>
                      {request.approvalHistory.map((history, index) => {
                        const isLast =
                          index === request.approvalHistory.length - 1;

                        return (
                          <Flex key={index} gap={4}>
                            <Box
                              flexShrink={0}
                              position="relative"
                              display="flex"
                              justifyContent="center"
                              minW="20px"
                            >
                              {!isLast && (
                                <Box
                                  position="absolute"
                                  top="20px"
                                  bottom="-20px"
                                  left="50%"
                                  transform="translateX(-50%)"
                                  width="2px"
                                  bg="gray.300"
                                />
                              )}
                              <Icon
                                as={
                                  history.action === "APPROVE" ||
                                  history.action === "BENCH_APPROVE"
                                    ? BsCheckCircleFill
                                    : history.action === "REJECT"
                                      ? BsXCircleFill
                                      : BsArrowCounterclockwise
                                }
                                boxSize={5}
                                color={
                                  history.action === "APPROVE"
                                    ? "green.500"
                                    : history.action === "BENCH_APPROVE"
                                      ? "yellow.500"
                                      : history.action === "REJECT"
                                        ? "red.500"
                                        : "orange.500"
                                }
                                bg={bgColor}
                                zIndex={1}
                              />
                            </Box>
                            <Box flex={1}>
                              <Flex
                                justify="space-between"
                                align="center"
                                mb={1}
                              >
                                <Text fontWeight="semibold" fontSize="sm">
                                  {history.stageName ||
                                    `Stage ${history.stage + 1}`}
                                </Text>
                                <Badge
                                  colorScheme={
                                    history.action === "APPROVE"
                                      ? "green"
                                      : history.action === "BENCH_APPROVE"
                                        ? "yellow"
                                        : history.action === "REJECT"
                                          ? "red"
                                          : "orange"
                                  }
                                >
                                  {history.action === "BENCH_APPROVE"
                                    ? "BENCH APPROVE"
                                    : history.action}
                                </Badge>
                              </Flex>
                              <Text fontSize="sm" color="gray.600">
                                {history.approverId?.name || "Unknown"} (
                                {history.approverId?.email})
                              </Text>
                              {history.action === "BENCH_APPROVE" &&
                                history.benchApprovedBy && (
                                  <Text fontSize="xs" color="yellow.600">
                                    Bench approved by:{" "}
                                    {history.benchApprovedBy?.name || "Unknown"}
                                  </Text>
                                )}
                              <Text fontSize="xs" color="gray.500">
                                {new Date(
                                  history.actionDate || history.createdAt,
                                ).toLocaleString()}
                              </Text>
                              {history.remarks && (
                                <Text fontSize="sm" mt={2} color="gray.700">
                                  <strong>Remarks:</strong> {history.remarks}
                                </Text>
                              )}
                            </Box>
                          </Flex>
                        );
                      })}
                    </VStack>
                  </Box>
                )}
            </CardBody>
          </Card>
        )}

        {/* Line Items Card */}
        <Card mb={6} bg={bgColor}>
          <CardHeader>
            <Heading size="md">Line Items</Heading>
          </CardHeader>
          <CardBody>
            <LineItemsTable
              lineItems={request.lineItems || []}
              setLineItems={() => {}}
              readOnly={true}
            />
          </CardBody>
        </Card>

        {/* Vendor Quotation Section - Show for all statuses */}
        <Card mb={6} bg={bgColor}>
          <CardBody>
            <VendorPOProposalList
              requestId={id}
              requestStatus={request.status}
              approvalHistory={request.approvalHistory || []}
              onStatusChange={(newStatus) => {
                setRequest({ ...request, status: newStatus });
              }}
            />
          </CardBody>
        </Card>

        {/* Comments Section */}
        <Box mb={6}>
          <CommentSection requestId={id} request={request} />
        </Box>

        {/* Metadata */}
        <Card bg={bgColor}>
          <CardBody>
            <Grid
              templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
              gap={4}
            >
              <GridItem>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                  Created By
                </Text>
                <Text>{request.createdBy?.name || "N/A"}</Text>
              </GridItem>
              <GridItem>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                  Created Date
                </Text>
                <Text>{new Date(request.createdAt).toLocaleString()}</Text>
              </GridItem>
              {request.updatedAt && (
                <GridItem>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                    Last Updated
                  </Text>
                  <Text>{new Date(request.updatedAt).toLocaleString()}</Text>
                </GridItem>
              )}
            </Grid>
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
};

export default RequestDetail;
